import { computed } from 'mobx';
import { PartialDeep } from 'type-fest';

import { translate } from 'services/i18nService';
import { AbstractTheme, CampaignFlagsMap, ControlledFeaturesMap, Theme } from 'themes/themes.types';
import { getThemePart } from 'themes/utils';

import birthdayBackground from './images/tile-birthday.png';
import diceIcon from './images/games/dice-icon-birthday.png';
import kenoIcon from './images/games/keno-icon-birthday.png';
import wheelIcon from './images/games/wheel-icon-birthday.png';
import boostIcon from './images/boost/boost-icon-birthday.png';
import logoImage from './images/logo-birthday.png';
import tippingTier1Sound from './sounds/tipping/birthday_tier_1.mp3';
import tippingTier2Sound from './sounds/tipping/birthday_tier_2.mp3';
import tippingTier3Sound from './sounds/tipping/birthday_tier_3.mp3';
import tippingTier4Sound from './sounds/tipping/birthday_tier_4.mp3';
import styles from './BirthdayTheme.css';


const SLOGAN_TRANSLATE_KEY = 'campaigns.birthday.token.heading';
const OFFER_TEXT = {
  ntp: 'campaigns.birthday.token.ntp',
  rest: 'campaigns.birthday.token.all',
};

function getOfferText(campaign: any) {
  /* the user is NOT logged in when window.hasPurchases is undefined */
  /* to do: refactor the logic to use `user.profileInfo` and `user.profileInfo.hasPurchases` from UserStore. */
  if (window.hasPurchases === undefined) {
    return translate(OFFER_TEXT.rest);
  }

  if (window.hasPurchases) {
    return translate(OFFER_TEXT.rest);
  }

  return translate(OFFER_TEXT.ntp, { 0: campaign?.assets?.bonusTokenAmountPlaceholder || '' });
}

export default class BirthdayTheme extends AbstractTheme {
  static CFS: ControlledFeaturesMap = {};

  static CAMPAIGN_FLAGS: CampaignFlagsMap = {
    GAMES: 'BIRTHDAY_GAMES',
    LOGO: 'BIRTHDAY_LOGO',
    BOOST: 'BIRTHDAY_BOOST',
    TIPPING: 'BIRTHDAY_TIPPING',
    ON_CAM_ANIMATION: 'BIRTHDAY_ON_CAM_ANIMATION',
    BUY_TOKENS_MODAL: 'BIRTHDAY_BUY_TOKENS_MODAL',
    HEADER_CTA: 'BIRTHDAY_HEADER_CTA',
    MOBILE_OVERLAY: 'BIRTHDAY_MOBILE_OVERLAY',
    INFO_STRIP: 'BIRTHDAY_INFO_STRIP',
    REGISTRATION_OVERLAY: 'BIRTHDAY_REGISTRATION_OVERLAY',
    DRAWER_MENU: 'BIRTHDAY_DRAWER_MENU_STRIP',
    CHAT_OFFER: 'BIRTHDAY_CHAT_OFFER',
  };

  @computed get theme(): PartialDeep<Theme> {
    return {
      games: getThemePart<'games', true>(BirthdayTheme.CAMPAIGN_FLAGS.GAMES, () => {
        return {
          keno: {
            icon: kenoIcon,
            borderClass: styles.kenoBorder,
            containerClass: styles.kenoContainer,
          },
          wheel: {
            icon: wheelIcon,
            containerClass: styles.wheelContainer,
            outerCircleClass: styles.wheelOuterCircle,
            arrowClass: styles.wheelArrow,
            centerClass: styles.wheelCenter,
          },
          dice: {
            icon: diceIcon,
            containerClass: styles.diceContainer,
          },
        };
      }),

      logoImage: getThemePart<'logoImage'>(BirthdayTheme.CAMPAIGN_FLAGS.LOGO, () => logoImage),

      boost: getThemePart<'boost', true>(BirthdayTheme.CAMPAIGN_FLAGS.BOOST, () => {
        return {
          icon: boostIcon,
          buttonIcon: boostIcon,
        };
      }),

      tipping: getThemePart<'tipping', true>(BirthdayTheme.CAMPAIGN_FLAGS.TIPPING, () => {
        return {
          tier1Sound: tippingTier1Sound,
          tier2Sound: tippingTier2Sound,
          tier3Sound: tippingTier3Sound,
          tier4Sound: tippingTier4Sound,
        };
      }),

      onCamAnimation: getThemePart<'onCamAnimation'>(BirthdayTheme.CAMPAIGN_FLAGS.ON_CAM_ANIMATION, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          animBackgroundClass: styles.animBackground,
          animBackground2Class: styles.animBackground2,
          staticBackgroundClass: styles.staticBackground,
          animWrapperClass: styles.animWrapper,
          animTextClass: styles.animText,
          line1Text: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
        };
      }),

      buyTokensModal: getThemePart<'buyTokensModal'>(BirthdayTheme.CAMPAIGN_FLAGS.BUY_TOKENS_MODAL, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          titleClass: styles.tokenOfferTitle,
          headerClass: styles.buyTokensModalHeader,
          title: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
          text: getOfferText(campaign),
        };
      }),

      headerCTA: getThemePart<'headerCTA'>(BirthdayTheme.CAMPAIGN_FLAGS.HEADER_CTA, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          wrapperClass: styles.headerCTAWrapper,
          title: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
        };
      }),

      mobileOverlay: getThemePart<'mobileOverlay'>(BirthdayTheme.CAMPAIGN_FLAGS.MOBILE_OVERLAY, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          titleClass: styles.tokenOfferTitle,
          title: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
          text: getOfferText(campaign),
          bodyClass: styles.mobileOverlayBody,
        };
      }),

      chatOffer: getThemePart<'chatOffer'>(BirthdayTheme.CAMPAIGN_FLAGS.CHAT_OFFER, (campaign) => {
        return {
          titleClass: styles.tokenOfferTitle,
          wrapperClass: styles.chatOfferWrapperClass,
          title: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
          text: getOfferText(campaign),
        };
      }),

      infoStrip: getThemePart<'infoStrip'>(BirthdayTheme.CAMPAIGN_FLAGS.INFO_STRIP, (campaign) => {
        return {
          campaignSecondsLeft: campaign.campaignDetails.timeLeft,
          titleClass: styles.tokenOfferTitle,
          wrapperClass: styles.infoStripWrapperClass,
          title: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
          text: getOfferText(campaign),
          useTimer: true,
        };
      }),

      registrationOverlay:
      getThemePart<'registrationOverlay'>(BirthdayTheme.CAMPAIGN_FLAGS.REGISTRATION_OVERLAY, (campaign) => {
        return {
          title: translate('campaigns.birthday.registration.title'),
          text: translate('campaigns.birthday.registration.text',
            {
              0: campaign.campaignDetails.bonusTokenPercent,
              1: '<i><u>',
              2: '</u></i>',
              3: campaign?.assets?.bonusTokenAmountPlaceholder || '',
            }
          ),
          titleClass: styles.registrationOverlayTitle,
          backgroundImage: birthdayBackground,
        };
      }),

      drawerMenu:
      getThemePart<'drawerMenu'>(BirthdayTheme.CAMPAIGN_FLAGS.DRAWER_MENU, (campaign) => {
        return {
          title: translate(SLOGAN_TRANSLATE_KEY, { 0: campaign.campaignDetails.bonusTokenPercent }),
          text: getOfferText(campaign),
          bodyClass: styles.drawerMenuContainer,
          titleClass: styles.drawerMenuTitle,
        };
      }),
    };
  }
}
